// WarningAckDialog.tsx — Structural check acknowledgment popup
// Shown when ui.pendingWarnings is non-null (after any dialog close).
// Advisory only — never blocks exports or other actions.
// NOT routed through DialogRouter/ActiveDialog; driven by pendingWarnings state directly.

import { useMemo } from 'react';
import { useShallowStore } from '../store/useStore';
import {
  countBySeverity,
  CATEGORY_LABELS,
  CATEGORIES,
  type StructuralWarning,
  type WarnCategory,
} from '../utils/structuralWarnings';

export default function WarningAckDialog() {
  const { warnings, clearPendingWarnings } = useShallowStore(s => ({
    warnings:             s.ui.pendingWarnings ?? [],
    clearPendingWarnings: s.clearPendingWarnings,
  }));

  const { errors, warns } = useMemo(() => countBySeverity(warnings), [warnings]);

  const byCategory = useMemo(() => {
    const map = new Map<WarnCategory, StructuralWarning[]>();
    for (const cat of CATEGORIES) map.set(cat, []);
    for (const w of warnings) map.get(w.category)!.push(w);
    return map;
  }, [warnings]);

  const headerClass = errors > 0 ? 'warn-ack-header error' : 'warn-ack-header warn';

  function headerLabel(): string {
    const parts: string[] = [];
    if (errors > 0) parts.push(`${errors} error${errors !== 1 ? 's' : ''}`);
    if (warns > 0)  parts.push(`${warns} warning${warns !== 1 ? 's' : ''}`);
    return parts.join(' · ');
  }

  return (
    <div className="warn-ack-overlay" role="dialog" aria-modal="true" aria-label="Structural Checks">
      <div className="warn-ack-box">
        <div className={headerClass}>
          <span className="warn-ack-icon">{errors > 0 ? '⛔' : '⚠️'}</span>
          <span className="warn-ack-title">Structural Checks</span>
          <span className="warn-ack-counts">{headerLabel()}</span>
        </div>

        <div className="warn-ack-list">
          {CATEGORIES.map(cat => {
            const items = byCategory.get(cat) ?? [];
            if (items.length === 0) return null;
            return (
              <div key={cat} className="warn-ack-category">
                <div className="warn-ack-cat-label">{CATEGORY_LABELS[cat]}</div>
                {items.map((w, i) => (
                  <div key={`${w.id}-${i}`} className={`warn-ack-item ${w.severity}`}>
                    <span className="warn-ack-item-icon">
                      {w.severity === 'error' ? '⛔' : '⚠️'}
                    </span>
                    <span className="warn-ack-item-msg">{w.message}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="warn-ack-footer">
          <span className="warn-ack-advisory">Advisory only — exports are not blocked.</span>
          <button className="warn-ack-btn" onClick={clearPendingWarnings} autoFocus>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
